import {
  Column,
  Columns,
  EmptyState,
  FlexBox,
  Spinner,
} from '@dixa/lib-shared-habla';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { ReactNode } from 'react';

import { analyticsApi } from 'apis/analytics';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useBreakpointValue } from 'utils/mediaQuery';

import styles from './analytics.view.module.scss';
import { AnalyticsPinPanelModal } from './components/panels/components/pin/analyticsPinPanelModal';
import { AnalyticsQuickViewModal } from './components/quickView/components/modal/quickViewModal';
import {
  AnalyticsSidenav,
  analyticsSidenavWidth,
} from './components/sidenav/analyticsSidenav';

export const AnalyticsView = ({ children }: PropsWithChildren): JSX.Element => {
  const { dashboards, isLoading, isUninitialized, isError, refetch } =
    analyticsApi.useReadDashboardsQuery(undefined, {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        dashboards: data?.data.dashboards,
      }),
    });

  const flags = useFeatureFlag(['analytics-custom-dashboards']);
  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  /** Renders the appropriate content based on the status of the API request. */
  const renderContent = (): ReactNode => {
    if (isUninitialized || isLoading) {
      return (
        <FlexBox
          flexDirection="column"
          height="100%"
          alignItems="center"
          justifyContent="center"
          gap="400"
        >
          <Spinner />

          <EmptyState
            title="Initializing..."
            description="Please wait a moment"
            icon={null}
          />
        </FlexBox>
      );
    }

    // When the request failed or no dashboards were returned.
    if (isError || (dashboards && dashboards.length === 0)) {
      return (
        <FlexBox
          flexDirection="column"
          height="100%"
          alignItems="center"
          justifyContent="center"
        >
          <EmptyState
            title="Something went wrong"
            description="We're experiencing some issues, try again or reload the page. If this issue persists, please contact support."
            icon="exclamation"
            iconColor="red.500"
            buttonProps={{
              children: 'Try again',
              onClick: refetch,
            }}
          />
        </FlexBox>
      );
    }

    return children;
  };

  return (
    <div className={styles.root}>
      <Columns>
        <Column size={isSmallScreen ? 0 : analyticsSidenavWidth}>
          <Column.Row size={1} noHorizontalPadding noVerticalPadding>
            <AnalyticsSidenav isExpandable={!isLoading} />
          </Column.Row>
        </Column>

        <Column>
          <Column.Row size={1} noHorizontalPadding noVerticalPadding>
            {renderContent()}
          </Column.Row>
        </Column>
      </Columns>

      <AnalyticsQuickViewModal />
      {flags.analyticsCustomDashboards && <AnalyticsPinPanelModal />}
    </div>
  );
};
